<script lang="ts">
	import { onMount } from 'svelte';
	import type { Report } from '#lib/types';
	import { t } from '#lib/i18n/state.svelte';
	import { copyText } from '#lib/ui/clipboard';
	import {
		badgeHtml,
		badgeMarkdown,
		badgeSvg,
		badgeText,
		createBadgeInfo,
		svgToPngBlob,
		type BadgeInfo,
		type BadgeSize,
		type BadgeTheme
	} from '#lib/badge/generate';

	interface Props {
		report: Report;
		onclose: () => void;
	}
	let { report, onclose }: Props = $props();

	let info = $state<BadgeInfo | null>(null);
	let theme = $state<BadgeTheme>('light');
	let size = $state<BadgeSize>('small');
	let copied = $state<string | null>(null);

	onMount(async () => {
		info = await createBadgeInfo(report);
	});

	const svg = $derived(info ? badgeSvg(info, theme, size) : '');
	const html = $derived(info ? badgeHtml(info, theme, size) : '');
	const markdown = $derived(info ? badgeMarkdown(info, theme, size) : '');
	const text = $derived(info ? badgeText(info) : '');

	async function copy(key: string, value: string) {
		if (await copyText(value)) {
			copied = key;
			setTimeout(() => (copied = null), 1600);
		}
	}

	function downloadSvg() {
		if (!info) return;
		const blob = new Blob([svg], { type: 'image/svg+xml' });
		const url = URL.createObjectURL(blob);
		const a = document.createElement('a');
		a.href = url;
		a.download = `repcheck-badge-${info.hash}.svg`;
		a.click();
		setTimeout(() => URL.revokeObjectURL(url), 1500);
	}

	async function downloadPng() {
		if (!info) return;
		try {
			const blob = await svgToPngBlob(svg);
			const url = URL.createObjectURL(blob);
			const a = document.createElement('a');
			a.href = url;
			a.download = `repcheck-badge-${info.hash}.png`;
			a.click();
			setTimeout(() => URL.revokeObjectURL(url), 1500);
		} catch {
			/* canvas unavailable */
		}
	}
</script>

<div class="overlay">
	<div class="modal" role="dialog" tabindex="-1" aria-modal="true" aria-label={t('badge.title')}>
		<header class="head">
			<div>
				<p class="eyebrow">{t('badge.eyebrow')}</p>
				<h2>{t('badge.title')}</h2>
				<p class="sub">{t('badge.sub')}</p>
			</div>
			<button class="btn btn-ghost btn-sm" onclick={onclose} aria-label={t('common.close')}>✕</button>
		</header>

		{#if info}
			<div class="preview">
				{@html svg}
			</div>
			<p class="id">{t('badge.id')}: <span class="mono">{info.id}</span></p>
			<p class="note">{t('badge.note')}</p>

			<div class="controls">
				<div class="seg">
					<button class="seg-btn" class:active={theme === 'light'} onclick={() => (theme = 'light')}>
						{t('badge.light')}
					</button>
					<button class="seg-btn" class:active={theme === 'dark'} onclick={() => (theme = 'dark')}>
						{t('badge.dark')}
					</button>
				</div>
				<div class="seg">
					<button class="seg-btn" class:active={size === 'small'} onclick={() => (size = 'small')}>
						{t('badge.small')}
					</button>
					<button class="seg-btn" class:active={size === 'large'} onclick={() => (size = 'large')}>
						{t('badge.large')}
					</button>
				</div>
				<div class="downloads">
					<button class="btn btn-sm" onclick={downloadSvg}>{t('badge.downloadSvg')}</button>
					<button class="btn btn-sm" onclick={downloadPng}>{t('badge.downloadPng')}</button>
				</div>
			</div>

			{#each [{ key: 'html', label: t('badge.html'), value: html }, { key: 'markdown', label: t('badge.markdown'), value: markdown }, { key: 'text', label: t('badge.text'), value: text }] as item (item.key)}
				<section class="code-block">
					<div class="code-head">
						<span>{item.label}</span>
						<button class="btn btn-sm" onclick={() => copy(item.key, item.value)}>
							{copied === item.key ? t('badge.copied') : t('badge.copy')}
						</button>
					</div>
					<textarea readonly rows={item.key === 'text' ? 2 : 3} value={item.value}></textarea>
				</section>
			{/each}
		{:else}
			<p class="note">{t('badge.loading')}</p>
		{/if}
	</div>
</div>

<style>
	.overlay {
		position: fixed;
		inset: 0;
		background: rgb(28 25 23 / 0.45);
		display: grid;
		place-items: center;
		padding: 1rem;
		z-index: 65;
	}
	.modal {
		width: min(46rem, 100%);
		max-height: 92vh;
		overflow: auto;
		background: var(--paper);
		border: 1px solid var(--line);
		border-radius: var(--radius);
		padding: 1.2rem;
		box-shadow: 0 24px 60px rgb(0 0 0 / 0.22);
	}
	.head {
		display: flex;
		justify-content: space-between;
		gap: 0.8rem;
	}
	.eyebrow {
		margin: 0 0 0.2rem;
		font-size: 0.68rem;
		text-transform: uppercase;
		letter-spacing: 0.1em;
		color: var(--muted);
		font-weight: 650;
	}
	h2 {
		margin: 0;
		font-size: 1.15rem;
	}
	.sub {
		margin: 0.2rem 0 0;
		color: var(--muted);
		font-size: 0.82rem;
	}
	.preview {
		display: grid;
		place-items: center;
		padding: 1.4rem;
		margin: 1rem 0 0.6rem;
		background: var(--paper-2);
		border: 1px solid var(--line);
		border-radius: 10px;
		overflow-x: auto;
	}
	.id {
		margin: 0.2rem 0;
		font-size: 0.8rem;
	}
	.mono {
		font-family: var(--font-mono, monospace);
		font-size: 0.76rem;
	}
	.note {
		margin: 0.2rem 0 0.8rem;
		font-size: 0.74rem;
		color: var(--muted);
	}
	.controls {
		display: flex;
		gap: 0.5rem;
		flex-wrap: wrap;
		align-items: center;
		margin-bottom: 0.8rem;
	}
	.seg {
		display: inline-flex;
		border: 1px solid var(--line-strong);
		border-radius: 8px;
		overflow: hidden;
		background: #fff;
	}
	.seg-btn {
		border: 0;
		background: transparent;
		padding: 0.35rem 0.6rem;
		font-size: 0.78rem;
		cursor: pointer;
		color: var(--muted);
	}
	.seg-btn.active {
		background: var(--ink);
		color: var(--paper);
	}
	.downloads {
		display: flex;
		gap: 0.35rem;
		flex-wrap: wrap;
	}
	.code-block {
		margin-bottom: 0.7rem;
	}
	.code-head {
		display: flex;
		justify-content: space-between;
		align-items: center;
		gap: 0.5rem;
		font-size: 0.78rem;
		font-weight: 600;
		margin-bottom: 0.25rem;
	}
	textarea {
		width: 100%;
		font-family: var(--font-mono, monospace);
		font-size: 0.7rem;
		border: 1px solid var(--line-strong);
		border-radius: 7px;
		padding: 0.45rem;
		background: #fff;
		resize: vertical;
	}
	@media (max-width: 560px) {
		.controls {
			align-items: stretch;
		}
		.downloads {
			width: 100%;
		}
		.downloads .btn {
			flex: 1 1 auto;
		}
	}
</style>
